import styled from "@emotion/styled/macro";
import { tokens } from "@sunrun/experience-ui-components";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

type PersistentTooltipDirection = "bottom" | "bottom-left" | "bottom-right";

type PersistentTooltipProps = {
  trigger: ReactNode;
  children: ReactNode;
  direction?: PersistentTooltipDirection;
  className?: string;
  isFullWidth?: boolean;
};

/**
 * A click-to-open popover that stays open until the user clicks away. Unlike the shared Tooltip component, it never
 * reacts to hover, which allows it contain interactive content like links.
 */
const PersistentTooltip = ({
  trigger,
  children,
  direction = "bottom",
  className,
  isFullWidth = false,
}: PersistentTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        close();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, close]);

  return (
    <Container
      ref={containerRef}
      className={className}
      isFullWidth={isFullWidth}
    >
      <Trigger
        type="button"
        aria-expanded={isOpen}
        isFullWidth={isFullWidth}
        onClick={() => setIsOpen((open) => !open)}
      >
        {trigger}
      </Trigger>
      {isOpen && (
        <>
          <Arrow />
          <Bubble role="dialog" $direction={direction}>
            {children}
          </Bubble>
        </>
      )}
    </Container>
  );
};

const Container = styled.div<{ isFullWidth?: boolean }>`
  position: relative;
  display: ${({ isFullWidth }) => (isFullWidth ? "flex" : "inline-flex")};
  width: ${({ isFullWidth }) => (isFullWidth ? "100%" : "auto")};
`;

const Trigger = styled.button<{ isFullWidth?: boolean }>`
  all: unset;
  cursor: pointer;
  display: ${({ isFullWidth }) => (isFullWidth ? "flex" : "inline-flex")};
  align-items: center;
  width: ${({ isFullWidth }) => (isFullWidth ? "100%" : "auto")};

  ${({ isFullWidth }) =>
    isFullWidth &&
    `
    & > * {
      width: 100%;
      box-sizing: border-box;
    }
  `}

  &:focus-visible {
    outline: 2px solid ${tokens.INFO_DARK};
    outline-offset: 2px;
    border-radius: 100px;
  }
`;

const Bubble = styled.div<{ $direction: PersistentTooltipDirection }>`
  position: absolute;
  top: calc(100% + 8px);
  z-index: 10;
  width: max-content;
  max-width: 280px;
  padding: 24px;
  border-radius: 12px;
  background-color: ${tokens.INFO_80};
  color: ${tokens.BRAND_HEROBLUE};
  box-shadow: 0 4px 16px rgba(24, 30, 58, 0.16);

  ${({ $direction }) =>
    $direction === "bottom-right"
      ? "right: 0;"
      : $direction === "bottom"
      ? "left: 50%; transform: translateX(-50%);"
      : "left: 0;"}
`;

const Arrow = styled.div`
  position: absolute;
  top: calc(100% + 1px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 11;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid ${tokens.INFO_80};
`;

export { PersistentTooltip };
export type { PersistentTooltipProps, PersistentTooltipDirection };
